import { ed25519 } from '@noble/curves/ed25519.js';
import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, hexToBytes, utf8ToBytes, concatBytes } from '@noble/hashes/utils.js';
import { canonicalize } from './canonical.js';

export { bytesToHex, hexToBytes };

/** Domain tags keep a signature for one purpose from being replayed as another. */
export type Domain = 'request' | 'entry' | 'checkpoint' | 'chain' | 'note_redeem';
const PREFIX = 'DAZZLE/v1/';

export const ZERO_HASH = '0'.repeat(64);

export function sha256Hex(data: Uint8Array | string): string {
  return bytesToHex(sha256(typeof data === 'string' ? utf8ToBytes(data) : data));
}

/** Hash of a value's canonical form, separated by domain. */
export function digest(domain: Domain, value: unknown): Uint8Array {
  return sha256(concatBytes(utf8ToBytes(PREFIX + domain + '\n'), utf8ToBytes(canonicalize(value))));
}

export function digestHex(domain: Domain, value: unknown): string {
  return bytesToHex(digest(domain, value));
}

export interface KeyPair {
  secretKey: string; // hex, 32 bytes
  publicKey: string; // hex, 32 bytes
}

export function generateKeyPair(): KeyPair {
  const sk = ed25519.utils.randomSecretKey();
  return { secretKey: bytesToHex(sk), publicKey: bytesToHex(ed25519.getPublicKey(sk)) };
}

export function publicKeyOf(secretKeyHex: string): string {
  return bytesToHex(ed25519.getPublicKey(hexToBytes(secretKeyHex)));
}

/** Sign a 32-byte message given as hex. */
export function signHex(msgHex: string, secretKeyHex: string): string {
  return bytesToHex(ed25519.sign(hexToBytes(msgHex), hexToBytes(secretKeyHex)));
}

export function verifyHex(sigHex: string, msgHex: string, publicKeyHex: string): boolean {
  try {
    if (!isHex(sigHex, 64) || !isHex(msgHex, 32) || !isHex(publicKeyHex, 32)) return false;
    return ed25519.verify(hexToBytes(sigHex), hexToBytes(msgHex), hexToBytes(publicKeyHex));
  } catch {
    return false;
  }
}

export function isHex(s: unknown, bytes?: number): s is string {
  if (typeof s !== 'string' || !/^[0-9a-f]*$/.test(s) || s.length % 2 !== 0) return false;
  return bytes === undefined || s.length === bytes * 2;
}
